import React from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomTooltip } from '@components';
import { CommissionChildItem } from '@api/types/commissions';
import {
  COHORT_STATUS_MAP,
  COMMISSION_TYPE_MAP,
  CommissionType,
  PRICING_METHODOLOGY_MAP,
} from '@constants/constants';
import DrawerComponent from '../../../../../components/Drawer/Drawer';
import LabeledValue from '../components/LabeledValue';
import { formatCurrency, formatPercentage, getPricingDetails } from '../utils';

interface CommissionsDetailsSidePanelProps {
  showCommissionsDetailsSidePanel: boolean;
  setShowCommissionsDetailsSidePanel: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRowData: CommissionChildItem;
}

const CommissionsDetailsSidePanel: React.FC<CommissionsDetailsSidePanelProps> = ({
  showCommissionsDetailsSidePanel,
  setShowCommissionsDetailsSidePanel,
  selectedRowData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const infoData = [
    { label: 'Referrer', value: selectedRowData.referrer },
    { label: '', value: '' },
    { label: 'Deal Name', value: selectedRowData.dealName },
    { label: 'Funding Month', value: selectedRowData.fundingMonth },
    {
      label: 'Gross Funding Amount (USD)',
      value: selectedRowData.grossFundingAmount,
      formatValue: formatCurrency,
    },
    {
      label: 'Collection (USD)',
      value: selectedRowData.grossRecoupAmount,
      formatValue: formatCurrency,
    },
    {
      label: 'Actual IRR ',
      value: selectedRowData.irr,
      formatValue: formatPercentage,
    },
    {
      label: 'MOIC',
      value: selectedRowData.moic,
      formatValue: formatPercentage,
    },
    {
      label: 'Pricing Methodology',
      value: PRICING_METHODOLOGY_MAP[selectedRowData.pricingMethodology],
    },
    ...getPricingDetails(selectedRowData.pricingMethodology, selectedRowData.interest),
    {
      label: 'Hurdle Rate',
      value: selectedRowData.hurdleRate,
      formatValue: formatPercentage,
    },
    {
      label: 'Commission %',
      value: `${formatPercentage(selectedRowData.commissionPercentage)} • ${selectedRowData.commissionType === CommissionType.COHORT_BASED ? `${selectedRowData.numberOfCohorts} Cohort` : COMMISSION_TYPE_MAP[selectedRowData.commissionType]}`,
    },
    {
      label: 'Status',
      value: COHORT_STATUS_MAP[selectedRowData.cohortStatus],
    },
  ];

  return (
    <DrawerComponent
      open={showCommissionsDetailsSidePanel}
      anchor="right"
      close={() => {
        setShowCommissionsDetailsSidePanel(false);
      }}
      height="98vh"
      zIndex={9999}
      backgroundColor="#F0F0F1"
    >
      <Box
        sx={{
          padding: '1.5rem',
          width: isMobile ? 'auto' : '600px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '1.375rem',
            fontWeight: 700,
            lineHeight: '1.875rem',
            color: '#6C6C6C',
          }}
        >
          Commissions Details
          <Button
            sx={{ padding: 0, minWidth: 'fit-content' }}
            onClick={() => {
              setShowCommissionsDetailsSidePanel(false);
            }}
          >
            <CloseIcon sx={{ color: '#162C36' }} />
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          margin: '1rem 1.5rem',
          width: isMobile ? 'auto' : '552px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <Box
          sx={{
            padding: '1rem',
            borderRadius: '1rem',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            {infoData.map(({ label, value, formatValue }) => (
              <LabeledValue key={label} label={label} value={value} formatValue={formatValue} />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            padding: '1rem',
            borderRadius: '1rem',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              color: '#162936',
              lineHeight: '1.25rem',
            }}
          >
            Final Commission Amount
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: '#6C6C6C',
              lineHeight: '1.25rem',
            }}
          >
            Commission ($) = Max(0,(Gross Funding Amount * (MOIC-1) * ((Actual IRR-Hurdle
            Rate)/Actual IRR) * Commissions rate))
          </Typography>
          <Box
            sx={{
              backgroundColor: '#2F736E14',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#162936',
                lineHeight: '1.25rem',
                alignItems: 'center',
                display: 'flex',
                gap: '0.25rem',
              }}
            >
              {formatCurrency(selectedRowData.commission)}

              {selectedRowData.commissionType === CommissionType.COHORT_BASED && (
                <CustomTooltip
                  content={
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#6C6C6C',
                      }}
                    >
                      This is for {selectedRowData.currentCohortNumber}/
                      {selectedRowData.numberOfCohorts} cohort month.
                    </div>
                  }
                  customStyle={{
                    '& .MuiTooltip-tooltip': {
                      borderRadius: '0.75rem',
                      bgcolor: '#F0F0F1',
                      backdropFilter: 'blur(5px)',
                      boxShadow: 2,
                      paddingX: '1rem',
                      paddingY: '0.75rem',
                      position: 'relative',
                      left: '-110px',
                      top: '-10px',
                    },
                  }}
                >
                  <InfoOutlinedIcon style={{ height: '20px', color: '#2F736E' }} />
                </CustomTooltip>
              )}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                fontSize: '0.75rem',
                color: '#6C6C6C',
                lineHeight: '1.25rem',
              }}
            >
              {`${formatCurrency(selectedRowData.grossFundingAmount)} * (${formatPercentage(selectedRowData.moic)} - 1) * ((${formatPercentage(selectedRowData.irr)} - ${formatPercentage(selectedRowData.hurdleRate)}) / ${formatPercentage(selectedRowData.irr)}) * ${formatPercentage(selectedRowData.commissionPercentage)}`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </DrawerComponent>
  );
};

export default CommissionsDetailsSidePanel;
